import { useCallback, useEffect, useRef, useState } from 'react';
import { confirmMfaEnrollmentRequest, enrollMfaRequest } from 'api/requests/auth-mfa-requests';
import IMfaEnrollmentPayload from 'types/auth/IMfaEnrollmentPayload';
import IToken from 'types/auth/IToken';
import { MfaFailure, resolveMfaFailure } from '../mfa-helpers';

interface UseMfaEnrollmentResult {
  payload: IMfaEnrollmentPayload;
  loading: boolean;
  submitting: boolean;
  failure: MfaFailure;
  retrySeconds: number;
  confirm: (code: string) => Promise<IToken>;
  clearFailure: () => void;
}

/**
 * Owns every API call in the enrollment flow.
 *
 * `onFatal` must be referentially stable (wrap it in useCallback) — it is an effect dependency, and
 * an unstable one would re-run the enroll effect.
 */
export const useMfaEnrollment = (mfaToken: string, onFatal: (messageKey: string) => void): UseMfaEnrollmentResult => {
  const [payload, setPayload] = useState<IMfaEnrollmentPayload>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<MfaFailure>(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const enrollStarted = useRef(false);

  // Every Enroll call issues a new secret and invalidates the previous one, so this fires exactly
  // once. React 17's StrictMode does not double-invoke effects (that is React 18), so the latch is
  // guarding remounts from route or parent churn — whose only symptom would be a CODE_INVALID on a
  // secret the user correctly scanned.
  useEffect(() => {
    if (enrollStarted.current || !mfaToken) {
      return;
    }
    enrollStarted.current = true;

    (async () => {
      try {
        const result = await enrollMfaRequest(mfaToken);
        setPayload(result);
      } catch (error) {
        // A failed Enroll leaves nothing to render, so this always goes back to login.
        onFatal(resolveMfaFailure(error).messageKey);
      } finally {
        setLoading(false);
      }
    })();
  }, [mfaToken, onFatal]);

  // Backoff countdown, one timeout per tick. Cleared on unmount so a redirect mid-countdown leaves
  // no live timer behind.
  useEffect(() => {
    if (retrySeconds <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setRetrySeconds(current => Math.max(0, current - 1)), 1000);
    return () => clearTimeout(timer);
  }, [retrySeconds]);

  const confirm = useCallback(
    async (code: string): Promise<IToken> => {
      setSubmitting(true);
      setFailure(null);
      try {
        return await confirmMfaEnrollmentRequest(mfaToken, code);
      } catch (error) {
        const resolved = resolveMfaFailure(error);
        if (resolved.action === 'back-to-login') {
          onFatal(resolved.messageKey);
          return null;
        }
        setFailure(resolved);
        setRetrySeconds(resolved.retryAfterSeconds);
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [mfaToken, onFatal],
  );

  const clearFailure = useCallback(() => setFailure(null), []);

  return { payload, loading, submitting, failure, retrySeconds, confirm, clearFailure };
};

export default useMfaEnrollment;
